import type { PersonObject } from "@mirage/service-people/types";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import type { Recommendation } from "@mirage/shared/search/recommendation";
import { isDefined } from "@mirage/shared/util/tiny-utils";
import { v5 as uuidv5 } from "uuid";
import type { SearchFilter } from "./search-filters";
import {
  peopleRecommendationToSearchFilter,
  SearchFilterKeyword,
  toFilterBinding,
} from "./search-filters";

export type PeopleRecommendation = PersonObject & {
  uuid: string;
};

const PEOPLE_RECOMMENDATION_NAMESPACE = "2ed6657d-e927-568b-95e1-2665a8aea6a2";
const DEACTIVATED_DISPLAY_NAME_KEYWORD = "Deactivated";

export function toPeopleRecommendation(
  personObject: PersonObject,
): PeopleRecommendation {
  return {
    uuid:
      personObject.uuid ??
      uuidv5(personObject.email, PEOPLE_RECOMMENDATION_NAMESPACE),
    ...personObject,
  };
}

export const removeDeactivatedAccounts = (
  person: PeopleRecommendation,
): boolean => {
  return (
    !person.displayName?.includes(DEACTIVATED_DISPLAY_NAME_KEYWORD) &&
    !person.isDeactivated
  );
};

export const extractValidPersonFromRecommendation = (
  rec: Recommendation,
): PersonObject | undefined => {
  const person = [
    rec.sender,
    rec.creator,
    rec.lastModifier,
    rec.filteredModifier?.author,
  ]
    .filter(isDefined)
    .find((p) => p?.email);

  if (!person) return;

  return {
    email: person.email || "",
    displayName: person.displayName,
    profilePhotoUrl: person.profilePhotoUrl,
  };
};

export const deduplicateAndMergeExtractedPeople = (
  extractedPeople: PersonObject[],
  peopleRecommendations: PersonObject[],
) => {
  const emailsMap = new Map<string, boolean>();
  const result: PersonObject[] = [];

  for (const person of peopleRecommendations) {
    emailsMap.set(person.email, true);
    result.push(person);
  }

  for (const extractedPerson of extractedPeople) {
    if (!emailsMap.has(extractedPerson.email)) {
      result.push(extractedPerson);
      emailsMap.set(extractedPerson.email, true);
    }
  }

  return result;
};

export function mergePeopleRecommendationIntoFilters(
  filters: SearchFilter[],
  peopleRecommendation: PeopleRecommendation,
): SearchFilter[] {
  return [
    ...filters,
    peopleRecommendationToSearchFilter(peopleRecommendation),
  ].filter(isDefined);
}

/**
 * We're relying on the intelligence of the two-stage search, and scoring to filter the results that
 * are not valid complete swaps.
 * We will update this in the future but it requires having the same scoring system/filtering system here
 * as well as in the source file to ensure we inteligently replace the correct term(s).
 */
export function replacePartialPeopleFilterText(
  query: string,
  scoredResult: {
    result: PeopleRecommendation;
  },
): string {
  if (!query || query.trim() === "") {
    return "";
  }

  const KEYWORD = SearchFilterKeyword.Person;
  const filterText = scoredResult.result.email;
  const filterBinding = toFilterBinding(KEYWORD, filterText);

  return filterBinding;
}

export function isScoredPeopleFilter(
  result: typeahead.ScoredResult,
): result is typeahead.ScoredPeopleFilter {
  return result.type === typeahead.ResultType.PeopleFilter;
}

export function isScoredPeopleRecommendation(
  result: typeahead.ScoredResult,
): result is typeahead.ScoredPeopleRecommendation {
  return result.type === typeahead.ResultType.PeopleRecommendation;
}

export function isScoredPeopleResult(
  result: typeahead.ScoredResult,
): result is
  | typeahead.ScoredPeopleFilter
  | typeahead.ScoredPeopleRecommendation {
  return isScoredPeopleFilter(result) || isScoredPeopleRecommendation(result);
}

export function isTaggedPeopleResult(
  result: typeahead.TaggedResult,
): result is typeahead.$Tagged<
  typeahead.ResultType.PeopleFilter | typeahead.ResultType.PeopleRecommendation
> {
  return (
    result.type === typeahead.ResultType.PeopleFilter ||
    result.type === typeahead.ResultType.PeopleRecommendation
  );
}
